import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FileText,
    LayoutDashboard,
    LogOut,
    Menu,
    User,
    Wallet,
    Sun,
    Moon,
    BarChart3,
    CreditCard,
    TrendingUp,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useColorMode } from '../theme/ColorModeSelect';
import LanguageSelectorDropdown from '../theme/LanguageSelectorDropdown';
import { useTraderPermissions } from '../hooks/useTraderPermissions';
import { ModulePermission, PermissionAction } from '../types/permissions';
import { useSystemConfiguration } from '../api/system-configuration/use-system-configuration';
import { useTranslation } from 'react-i18next';

interface SidebarItem {
    icon: React.ReactNode;
    labelKey: keyof typeof SIDEBAR_LABEL_KEYS;
    path: string;
    badge?: number;
    permission?: { module: ModulePermission; action: PermissionAction };
}

const SIDEBAR_LABEL_KEYS = {
    trading: 'trading',
    accounts: 'accounts',
    orders: 'orders',
    trades: 'trades',
    transactions: 'transactions',
    account: 'account',
} as const;

const TradingSidebar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation('sidebar');
    const { logout } = useAuth();
    const { mode, setMode } = useColorMode();
    const { hasPermission } = useTraderPermissions();
    const { data: systemConfig } = useSystemConfiguration();
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const sidebarItems: SidebarItem[] = [
        {
            icon: <TrendingUp className="h-5 w-5" />,
            labelKey: 'trading',
            path: '/trade',
        },
        {
            icon: <Wallet className="h-5 w-5" />,
            labelKey: 'accounts',
            path: '/',
        },
        {
            icon: <FileText className="h-5 w-5" />,
            labelKey: 'orders',
            path: '/orders',
            permission: { module: ModulePermission.ORDERS, action: PermissionAction.VIEW },
        },
        {
            icon: <BarChart3 className="h-5 w-5" />,
            labelKey: 'trades',
            path: '/trades',
            permission: { module: ModulePermission.TRADES, action: PermissionAction.VIEW },
        },
        {
            icon: <CreditCard className="h-5 w-5" />,
            labelKey: 'transactions',
            path: '/transactions',
            permission: { module: ModulePermission.TRANSACTIONS, action: PermissionAction.VIEW },
        },
        {
            icon: <User className="h-5 w-5" />,
            labelKey: 'account',
            path: '/profile',
        },
    ];

    // Filter sidebar items based on permissions
    const visibleSidebarItems = sidebarItems.filter(item => {
        if (!item.permission) return true; // Always show items without permission requirements
        return hasPermission(item.permission.module, item.permission.action);
    });

    const handleNavigation = (path: string) => {
        navigate(path);
        // Close mobile drawer after navigation
        setMobileDrawerOpen(false);
    };

    const handleSignOut = () => {
        // Call the proper logout function from AuthContext
        logout();
        setMobileDrawerOpen(false);
    };

    const handleThemeToggle = () => {
        setMode(mode === 'dark' ? 'light' : 'dark');
    };

    const isActive = (path: string) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname === path;
    };

    const toggleMobileDrawer = () => {
        setMobileDrawerOpen(!mobileDrawerOpen);
    };

    // Sidebar content component
    const SidebarContent = ({ isMobileDrawer = false }: { isMobileDrawer?: boolean }) => (
        <div
            className={`${
                isMobileDrawer ? 'w-70' : 'w-16'
            } glass-medium border-r flex flex-col h-full`}
            style={{ borderColor: 'var(--card-border-color)' }}
        >
            {/* Logo - Fixed at top */}
            <div className="flex-shrink-0 py-2 px-2">
                {systemConfig?.system_logo_url || systemConfig?.logo_url ? (
                    <img
                        src={systemConfig.system_logo_url || systemConfig.logo_url}
                        alt={systemConfig.system_name || t('quantix')}
                        className={`${
                            isMobileDrawer ? 'w-auto max-w-[140px] px-2' : 'w-10 h-10'
                        } object-contain mb-6 cursor-pointer`}
                        onClick={() => handleNavigation('/')}
                    />
                ) : (
                    <div
                        className={`${
                            isMobileDrawer ? 'w-auto px-2' : 'w-10'
                        } h-10 rounded bg-gradient-to-br from-sidebar-primary to-sidebar-primary/80 flex items-center justify-center mb-6 font-bold text-xl text-sidebar-primary-foreground cursor-pointer`}
                        onClick={() => handleNavigation('/')}
                    >
                        <span className="font-bold">
                            {isMobileDrawer
                                ? systemConfig?.system_name || t('quantixTrading')
                                : (systemConfig?.system_name || t('quantix'))
                                      .charAt(0)
                                      .toUpperCase()}
                        </span>
                    </div>
                )}
            </div>

            {/* Scrollable Navigation Items */}
            <div className="flex-1 overflow-y-auto space-y-1 w-full px-2">
                {visibleSidebarItems.map(item =>
                    isMobileDrawer ? (
                        // Mobile Drawer Layout
                        <div
                            key={item.path}
                            onClick={() => handleNavigation(item.path)}
                            className={`flex items-center text-sidebar-foreground min-h-12 rounded cursor-pointer transition-colors px-2 py-1 ${
                                isActive(item.path)
                                    ? 'bg-sidebar-accent'
                                    : 'hover:bg-sidebar-accent/50'
                            }`}
                        >
                            <div className="flex items-center justify-center min-w-6 mr-2">
                                {item.badge ? (
                                    <div className="relative">
                                        {item.icon}
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                            {item.badge}
                                        </span>
                                    </div>
                                ) : (
                                    item.icon
                                )}
                            </div>
                            <span className="text-sm flex-1">{t(item.labelKey)}</span>
                            {isActive(item.path) && (
                                <div className="w-1.5 h-1.5 rounded-full bg-sidebar-primary" />
                            )}
                        </div>
                    ) : (
                        // Desktop Sidebar Layout with Tooltip
                        <div key={item.path} className="relative group w-full">
                            <button
                                onClick={() => handleNavigation(item.path)}
                                className={`text-sidebar-foreground w-12 h-12 rounded transition-colors relative flex items-center justify-center mx-auto ${
                                    isActive(item.path)
                                        ? 'bg-sidebar-accent'
                                        : 'hover:bg-sidebar-accent/50'
                                }`}
                                title={t(item.labelKey)}
                            >
                                {item.badge ? (
                                    <div className="relative">
                                        {item.icon}
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                            {item.badge}
                                        </span>
                                    </div>
                                ) : (
                                    item.icon
                                )}
                                {isActive(item.path) && (
                                    <div className="absolute -left-4 top-1/2 transform -translate-y-1/2 w-0.5 h-6 bg-sidebar-primary rounded-r" />
                                )}
                            </button>

                            {/* Tooltip */}
                            <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-popover text-popover-foreground text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 border border-border shadow-lg">
                                {t(item.labelKey)}
                                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-l-0 border-r-4 border-t-2 border-b-2 border-transparent border-r-popover"></div>
                            </div>
                        </div>
                    )
                )}
            </div>

            {/* Theme Toggle */}
            <div className="px-2">
                {isMobileDrawer ? (
                    // Mobile Theme Toggle
                    <div
                        onClick={handleThemeToggle}
                        className="flex items-center text-sidebar-foreground min-h-12 rounded cursor-pointer hover:bg-sidebar-accent/50 transition-colors px-2 py-1 w-full mb-2"
                    >
                        <div className="flex items-center justify-center min-w-6 mr-2">
                            {mode === 'dark' ? (
                                <Sun className="h-5 w-5" />
                            ) : (
                                <Moon className="h-5 w-5" />
                            )}
                        </div>
                        <span className="text-sm">
                            {mode === 'dark' ? t('lightMode') : t('darkMode')}
                        </span>
                    </div>
                ) : (
                    // Desktop Theme Toggle with Tooltip
                    <div className="relative group w-full mb-2">
                        <button
                            onClick={handleThemeToggle}
                            className="text-sidebar-foreground w-12 h-12 rounded hover:bg-sidebar-accent/50 transition-colors flex items-center justify-center mx-auto"
                            title={mode === 'dark' ? t('switchToLightMode') : t('switchToDarkMode')}
                        >
                            {mode === 'dark' ? (
                                <Sun className="h-5 w-5" />
                            ) : (
                                <Moon className="h-5 w-5" />
                            )}
                        </button>

                        {/* Tooltip */}
                        <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-popover text-popover-foreground text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 border border-border shadow-lg">
                            {mode === 'dark' ? t('lightMode') : t('darkMode')}
                            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-l-0 border-r-4 border-t-2 border-b-2 border-transparent border-r-popover"></div>
                        </div>
                    </div>
                )}

                {/* Language selector */}
                {isMobileDrawer ? (
                    <div className="px-2 py-1 mb-2">
                        <LanguageSelectorDropdown />
                    </div>
                ) : (
                    <div className="relative group w-full mb-2">
                        <LanguageSelectorDropdown compact />
                    </div>
                )}

                {/* Sign Out */}
                {isMobileDrawer ? (
                    // Mobile Sign Out
                    <div
                        onClick={handleSignOut}
                        className="flex items-center text-sidebar-foreground min-h-12 rounded cursor-pointer hover:bg-sidebar-accent/50 transition-colors px-2 py-1 w-full"
                    >
                        <div className="flex items-center justify-center min-w-6 mr-2">
                            <LogOut className="h-5 w-5" />
                        </div>
                        <span className="text-sm">{t('signOut')}</span>
                    </div>
                ) : (
                    // Desktop Sign Out with Tooltip
                    <div className="relative group w-full">
                        <button
                            onClick={handleSignOut}
                            className="text-sidebar-foreground w-12 h-12 rounded hover:bg-sidebar-accent/50 transition-colors flex items-center justify-center mx-auto"
                            title={t('signOut')}
                        >
                            <LogOut className="h-5 w-5" />
                        </button>

                        {/* Tooltip */}
                        <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-popover text-popover-foreground text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 border border-border shadow-lg">
                            {t('signOut')}
                            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-l-0 border-r-4 border-t-2 border-b-2 border-transparent border-r-popover"></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <div className="hidden md:block w-16 left-0 top-0 bottom-0 z-50">
                <SidebarContent />
            </div>

            {/* Mobile Floating Action Button */}
            <button
                onClick={toggleMobileDrawer}
                className="md:hidden fixed top-4 left-4 z-50 trader-fab-gradient text-white hover:opacity-90 transition-opacity w-12 h-12 rounded-full shadow-lg"
            >
                <Menu className="h-5 w-5" />
            </button>

            {/* Mobile Drawer */}
            {mobileDrawerOpen && (
                <div className="md:hidden fixed inset-0 z-50">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setMobileDrawerOpen(false)}
                    />
                    <div className="absolute left-0 top-0 bottom-0">
                        <SidebarContent isMobileDrawer />
                    </div>
                </div>
            )}
        </>
    );
};

export default TradingSidebar;
